import React from "react";
import {
  Banner,
  Button,
  EmptyState,
  Flex,
  Link,
  LoadingIndicator,
  SlideoutDrawerV2,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  Tag,
  Text,
} from "@jsq/ds/core";
import { formatDocumentType, formatReceivedAt } from "../IncomingUploads/format";
import { buildCatalogUrl, DEFAULT_CATALOG_BASE_URL } from "./catalogUrl";
import { deriveChecklist, type ChecklistStepState } from "./deriveChecklist";
import * as styles from "./DocumentDetail.css";
import type { DocumentDetailStatus } from "../../hooks/useDocumentDetail";
import type { ExtractedField } from "../../hooks/useExtractedFields";

export const DOCUMENT_DETAIL_COPY = {
  fallbackTitle: "Document",
  fieldsHeading: "Extracted fields",
  fieldsPlaceholder: "No extracted fields for this document.",
  fieldsError: "Couldn't load extracted fields.",
  download: "Download original PDF",
  viewInDataCatalog: "View in Data Catalog",
  viewLineage: "View lineage",
  unavailableForType: "Not available for this document type",
  downloadError: "Couldn't get a download link.",
  notFoundTitle: "Document not found",
  notFoundDescription: "This document may have been removed or the link is out of date.",
  errorTitle: "Couldn't load this document",
} as const;

type TagVariant = "neutral" | "success" | "informative" | "danger";
const STEP_TAG: Record<ChecklistStepState, { variant: TagVariant; label: string }> = {
  done: { variant: "success", label: "Done" },
  in_progress: { variant: "informative", label: "Running" },
  failed: { variant: "danger", label: "Failed" },
  pending: { variant: "neutral", label: "Pending" },
};

const formatConfidence = (c: number | null | undefined): string =>
  c == null ? "" : `${Math.round(c <= 1 ? c * 100 : c)}%`;

type FieldColumn = {
  header: string;
  render: (f: ExtractedField) => React.ReactNode;
  rightAligned?: boolean;
  secondary?: boolean;
};

// Field + Value are the stable core; the three below are contingent (a dex pipeline
// redesign may drop them) — each is a single self-contained column entry, so dropping
// any one is a one-entry deletion.
const FIELD_COLUMNS: FieldColumn[] = [
  { header: "Field", render: (f) => f.fieldName },
  { header: "Value", render: (f) => f.value },
  { header: "Type", render: (f) => f.dataType, secondary: true },
  {
    header: "Confidence",
    render: (f) => formatConfidence(f.confidence),
    rightAligned: true,
    secondary: true,
  },
  { header: "Page", render: (f) => f.page ?? "", rightAligned: true, secondary: true },
];

export interface DocumentDetailProps {
  status: DocumentDetailStatus | null;
  loading: boolean;
  error?: boolean;
  onClose: () => void;
  onDownload: () => void;
  downloadDisabled?: boolean;
  downloadLoading?: boolean;
  downloadError?: boolean;
  fields?: ExtractedField[];
  fieldsLoading?: boolean;
  fieldsError?: boolean;
  /** Catalog/lineage link base; defaults to the test catalog — host must override per-environment. */
  catalogBaseUrl?: string;
}

export const DocumentDetail = ({
  status,
  loading,
  error = false,
  onClose,
  onDownload,
  downloadDisabled = true,
  downloadLoading = false,
  downloadError = false,
  fields = [],
  fieldsLoading = false,
  fieldsError = false,
  catalogBaseUrl = DEFAULT_CATALOG_BASE_URL,
}: DocumentDetailProps) => {
  const title = status?.originalFilename ?? DOCUMENT_DETAIL_COPY.fallbackTitle;
  const steps = status ? deriveChecklist(status.stage, status.status) : [];

  // cache-and-network: show cached fields over a refetch's loading/error.
  const renderFields = () => {
    if (fields.length > 0) {
      return (
        <Table>
          <TableHeader>
            <TableRow>
              {FIELD_COLUMNS.map((col) => (
                <TableCell key={col.header} header rightAligned={col.rightAligned}>
                  {col.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {fields.map((field, i) => (
              <TableRow key={`${field.fieldName}-${i}`}>
                {FIELD_COLUMNS.map((col) => {
                  const content = col.render(field);
                  return (
                    <TableCell key={col.header} rightAligned={col.rightAligned}>
                      {col.secondary ? <Text variant="secondary-sm">{content}</Text> : content}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      );
    }
    if (fieldsLoading) {
      return (
        <Flex justifyContent="center" data-testid="fields-loading">
          <LoadingIndicator />
        </Flex>
      );
    }
    if (fieldsError) {
      return <Banner variant="danger" title={DOCUMENT_DETAIL_COPY.fieldsError} />;
    }
    return (
      <EmptyState
        illustration="casual-documents"
        description={DOCUMENT_DETAIL_COPY.fieldsPlaceholder}
      />
    );
  };

  const renderBody = () => {
    if (loading && !status) {
      return (
        <Flex justifyContent="center" data-testid="detail-loading">
          <LoadingIndicator />
        </Flex>
      );
    }
    if (error && !status) {
      return <Banner variant="danger" title={DOCUMENT_DETAIL_COPY.errorTitle} />;
    }
    if (!status) {
      return (
        <EmptyState
          illustration="casual-documents"
          title={DOCUMENT_DETAIL_COPY.notFoundTitle}
          description={DOCUMENT_DETAIL_COPY.notFoundDescription}
        />
      );
    }
    const documentType = status.documentType;
    const catalogUrl = documentType && buildCatalogUrl(catalogBaseUrl, documentType, "schema");
    const lineageUrl = documentType && buildCatalogUrl(catalogBaseUrl, documentType, "lineage");

    return (
      <>
        <section aria-label="Pipeline progress" className={styles.stepList}>
          {steps.map((step) => (
            <div key={step.key}>
              <div className={styles.stepRow}>
                <Text>{step.label}</Text>
                <Tag
                  variant={STEP_TAG[step.state].variant}
                  label={STEP_TAG[step.state].label}
                />
              </div>
              {step.state === "failed" && status.errorDetail && (
                <Text variant="secondary-sm" className={styles.stepError}>
                  {status.errorDetail}
                </Text>
              )}
            </div>
          ))}
        </section>

        <section aria-label={DOCUMENT_DETAIL_COPY.fieldsHeading}>
          <Text variant="h3">{DOCUMENT_DETAIL_COPY.fieldsHeading}</Text>
          {renderFields()}
        </section>

        <section aria-label="Actions" className={styles.actions}>
          <Button
            variant="neutral"
            icon="download"
            iconPosition="left"
            onClick={onDownload}
            disabled={downloadDisabled}
            isLoading={downloadLoading}
            data-testid="download-pdf"
          >
            {DOCUMENT_DETAIL_COPY.download}
          </Button>
          {downloadError && (
            <Banner variant="danger" title={DOCUMENT_DETAIL_COPY.downloadError} />
          )}
          {catalogUrl ? (
            <Link
              href={catalogUrl}
              displayAsButton
              variant="neutral"
              shouldOpenInNewTab
              className={styles.actionLink}
              data-testid="view-catalog"
            >
              {DOCUMENT_DETAIL_COPY.viewInDataCatalog}
            </Link>
          ) : (
            <Button
              variant="neutral"
              disabled
              title={DOCUMENT_DETAIL_COPY.unavailableForType}
              data-testid="view-catalog"
            >
              {DOCUMENT_DETAIL_COPY.viewInDataCatalog}
            </Button>
          )}
          {lineageUrl ? (
            <Link
              href={lineageUrl}
              displayAsButton
              variant="neutral"
              shouldOpenInNewTab
              className={styles.actionLink}
              data-testid="view-lineage"
            >
              {DOCUMENT_DETAIL_COPY.viewLineage}
            </Link>
          ) : (
            <Button
              variant="neutral"
              disabled
              title={DOCUMENT_DETAIL_COPY.unavailableForType}
              data-testid="view-lineage"
            >
              {DOCUMENT_DETAIL_COPY.viewLineage}
            </Button>
          )}
        </section>
      </>
    );
  };

  return (
    <SlideoutDrawerV2 isOpen onClose={onClose}>
      <SlideoutDrawerV2.ComplexHeader
        title={title}
        subtitles={
          status
            ? [formatDocumentType(status.documentType), formatReceivedAt(status.receivedAt)]
            : []
        }
      />
      <SlideoutDrawerV2.Body className={styles.body}>{renderBody()}</SlideoutDrawerV2.Body>
    </SlideoutDrawerV2>
  );
};
